import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTopicLabel } from './useTopicLabel';
import { fetchTopicCandidateBreakdown } from '../../api/podcasts';
import {
    getColorForCandidate,
    getHeatmapColor,
} from './electionColors';

interface HeatmapTabProps {
    selectedRange: {
        from: string;
        to: string;
    };
    includeRadio: boolean;
    isActive: boolean;
}

type ViewMode = 'percentage' | 'count';
type PeriodFilter = 'inherited' | 'pre1raVuelta' | 'entre1raY2da';

// Date ranges for special periods
const PERIOD_RANGES = {
    pre1raVuelta: {
        from: '2025-06-29T04:00:00.000Z',
        to: '2025-11-23T03:59:59.999Z',
        label: 'Pre 1ra Vuelta',
    },
    entre1raY2da: {
        from: '2025-11-23T04:00:00.000Z',
        to: '2025-12-15T03:59:59.999Z',
        label: 'Entre 1ra y 2da Vuelta',
        restrictedCandidates: ['José Antonio Kast', 'Jeannette Jara'],
    },
};


export default function HeatmapTab({ selectedRange, includeRadio, isActive }: HeatmapTabProps) {
    const theme = useTheme();
    const { t } = useTranslation('elecciones');
    const topicLabel = useTopicLabel();
    const [userSelectedCandidates, setUserSelectedCandidates] = useState<string[] | null>(null);
    const [userSelectedTopics, setUserSelectedTopics] = useState<string[] | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('percentage');
    const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('inherited');

    // Compute effective range and restrictions based on period filter
    const { effectiveRange, restrictedCandidates } = useMemo(() => {
        if (periodFilter === 'inherited') {
            return { effectiveRange: selectedRange, restrictedCandidates: undefined };
        }
        const period = PERIOD_RANGES[periodFilter];
        return {
            effectiveRange: { from: period.from, to: period.to },
            restrictedCandidates: 'restrictedCandidates' in period ? period.restrictedCandidates : undefined,
        };
    }, [periodFilter, selectedRange]);

    // Reset candidate selection when period changes (to respect restrictions)
    React.useEffect(() => {
        setUserSelectedCandidates(null);
    }, [periodFilter]);

    const { data: breakdownData, isLoading, error } = useQuery({
        queryKey: ['topic-candidate-breakdown', effectiveRange.from, effectiveRange.to, includeRadio],
        queryFn: () => fetchTopicCandidateBreakdown({
            from: effectiveRange.from,
            to: effectiveRange.to,
            includeRadio,
        }),
        enabled: isActive,
        staleTime: 5 * 60 * 1000,
    });

    // Get all topics from API (already limited to top 10 by backend)
    const allTopics = useMemo(() => {
        if (!breakdownData?.topics) return [];
        return breakdownData.topics.map(t => t.topic);
    }, [breakdownData]);

    // Get all candidates sorted by total mentions
    const allCandidates = useMemo(() => {
        if (!breakdownData?.topics) return [];

        const candidateTotals = new Map<string, number>();
        breakdownData.topics.forEach(topic => {
            Object.entries(topic.candidates).forEach(([name, data]) => {
                // If restricted, only include those candidates
                if (restrictedCandidates && !restrictedCandidates.includes(name)) return;
                candidateTotals.set(name, (candidateTotals.get(name) || 0) + data.count);
            });
        });

        return Array.from(candidateTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);
    }, [breakdownData, restrictedCandidates]);

    // Derive effective selected candidates (user selection or default)
    const selectedCandidates = useMemo(() => {
        if (userSelectedCandidates !== null) return userSelectedCandidates;
        if (restrictedCandidates) return allCandidates;
        return allCandidates.slice(0, 6);
    }, [userSelectedCandidates, allCandidates, restrictedCandidates]);

    // Derive effective selected topics (user selection or default)
    const selectedTopics = useMemo(() => {
        if (userSelectedTopics !== null) return userSelectedTopics;
        return allTopics;
    }, [userSelectedTopics, allTopics]);

    const setSelectedCandidates = setUserSelectedCandidates;
    const setSelectedTopics = setUserSelectedTopics;

    // Filter topics based on selection
    const filteredTopics = useMemo(() => {
        if (!breakdownData?.topics) return [];
        if (selectedTopics.length === 0) return breakdownData.topics;
        return breakdownData.topics.filter(t => selectedTopics.includes(t.topic));
    }, [breakdownData, selectedTopics]);

    // Build heatmap data
    const heatmapData = useMemo(() => {
        if (!filteredTopics.length || !selectedCandidates.length) return { rows: [], maxValue: 0 };

        let maxValue = 0;

        const rows = filteredTopics.map(topic => {
            const row: any = { topic: topic.topic, totalMentions: topic.totalMentions };

            selectedCandidates.forEach(candidate => {
                const data = topic.candidates[candidate];
                const value = viewMode === 'percentage' ? (data?.percentage || 0) : (data?.count || 0);
                row[candidate] = value;
                row[`${candidate}_count`] = data?.count || 0;
                row[`${candidate}_pct`] = data?.percentage || 0;
                if (value > maxValue) maxValue = value;
            });

            return row;
        });

        return { rows, maxValue };
    }, [filteredTopics, selectedCandidates, viewMode]);

    // Get displayed candidates (filtered and sorted)
    const displayedCandidates = useMemo(() => {
        return allCandidates.filter(c => selectedCandidates.includes(c));
    }, [allCandidates, selectedCandidates]);

    const handleCandidateChange = (event: any) => {
        const value = event.target.value;
        setSelectedCandidates(typeof value === 'string' ? value.split(',') : value);
    };

    const handleTopicChange = (event: any) => {
        const value = event.target.value;
        setSelectedTopics(typeof value === 'string' ? value.split(',') : value);
    };

    if (!isActive) {
        return null;
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {t('empty.loadFailed', { message: (error as Error).message })}
            </Alert>
        );
    }

    if (!heatmapData.rows.length) {
        return (
            <Alert severity="info">
                {t('empty.noPeriod')}
            </Alert>
        );
    }

    return (
        <Box>
            {/* Filters */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                {/* Period Filter */}
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel>{t('period.label')}</InputLabel>
                    <Select
                        value={periodFilter}
                        onChange={(e) => setPeriodFilter(e.target.value as PeriodFilter)}
                        input={<OutlinedInput label={t('period.label')} />}
                    >
                        <MenuItem value="inherited">{t('period.selected')}</MenuItem>
                        <MenuItem value="pre1raVuelta">{t('period.beforeFirstRound')}</MenuItem>
                        <MenuItem value="entre1raY2da">{t('period.betweenRounds')}</MenuItem>
                    </Select>
                </FormControl>

                {/* Candidate Filter */}
                <FormControl sx={{ flex: 1 }}>
                    <InputLabel>{t('fields.candidates')}</InputLabel>
                    <Select
                        multiple
                        value={selectedCandidates}
                        onChange={handleCandidateChange}
                        input={<OutlinedInput label={t('fields.candidates')} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.slice(0, 3).map((value) => (
                                    <Chip
                                        key={value}
                                        label={value}
                                        size="small"
                                        sx={{
                                            backgroundColor: getColorForCandidate(value, allCandidates.indexOf(value)),
                                            color: 'white'
                                        }}
                                    />
                                ))}
                                {selected.length > 3 && (
                                    <Chip label={`+${selected.length - 3}`} size="small" />
                                )}
                            </Box>
                        )}
                    >
                        {allCandidates.map((candidate, idx) => (
                            <MenuItem key={candidate} value={candidate}>
                                <Checkbox checked={selectedCandidates.includes(candidate)} />
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: getColorForCandidate(candidate, idx),
                                        mr: 1
                                    }}
                                />
                                <ListItemText primary={candidate} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Topic Filter */}
                <FormControl sx={{ flex: 1 }}>
                    <InputLabel>{t('fields.topics')}</InputLabel>
                    <Select
                        multiple
                        value={selectedTopics}
                        onChange={handleTopicChange}
                        input={<OutlinedInput label={t('fields.topics')} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.slice(0, 3).map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                ))}
                                {selected.length > 3 && (
                                    <Chip label={`+${selected.length - 3}`} size="small" />
                                )}
                            </Box>
                        )}
                    >
                        {allTopics.map((topic) => (
                            <MenuItem key={topic} value={topic}>
                                <Checkbox checked={selectedTopics.includes(topic)} />
                                <ListItemText primary={topic} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* View Mode Toggle */}
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, value) => value && setViewMode(value)}
                    size="small"
                >
                    <ToggleButton value="percentage">%</ToggleButton>
                    <ToggleButton value="count">#</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {/* Description */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: 'action.hover',
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: '#3B82F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Typography sx={{ color: 'white', fontSize: 16 }}>💡</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.info.light }}>
                    {viewMode === 'percentage'
                        ? t('heatmap.pctHelp')
                        : t('heatmap.countHelp')}
                </Typography>
            </Paper>

            {/* Heatmap */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <Box sx={{
                    px: 3,
                    py: 2.5,
                    borderBottom: 1, borderColor: 'divider',
                    bgcolor: 'action.hover',
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em',
                        }}
                    >
                        {t('heatmap.title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                        {viewMode === 'percentage'
                            ? t('heatmap.pctSubtitle')
                            : t('heatmap.countSubtitle')}
                    </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 600 }}>
                            {/* Header row */}
                            <Box sx={{
                                display: 'flex',
                                borderBottom: 2, borderColor: 'divider',
                                pb: 1.5,
                                mb: 0,
                                backgroundColor: 'action.hover',
                                borderRadius: '8px 8px 0 0',
                                mx: -1,
                                px: 1,
                            }}>
                                <Box sx={{
                                    width: 160,
                                    flexShrink: 0,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: theme.palette.text.primary,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    py: 1.5,
                                    pl: 1,
                                }}>
                                    {t('heatmap.topic')}
                                </Box>
                                {displayedCandidates.map((candidate, idx) => (
                                    <Box
                                        key={candidate}
                                        sx={{
                                            flex: 1,
                                            minWidth: 90,
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            fontSize: 10,
                                            px: 0.5,
                                            py: 1.5,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {candidate.split(' ').slice(-1)[0]}
                                    </Box>
                                ))}
                            </Box>

                            {/* Data rows */}
                            {heatmapData.rows.map((row, rowIdx) => (
                                <Box
                                    key={row.topic}
                                    sx={{
                                        display: 'flex',
                                        borderBottom: rowIdx === heatmapData.rows.length - 1 ? 'none' : `1px solid ${theme.palette.divider}`,
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(59, 130, 246, 0.04)',
                                            transform: 'scale(1.002)',
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 160,
                                            flexShrink: 0,
                                            py: 1.5,
                                            px: 1.5,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: theme.palette.text.primary,
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: rowIdx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.04)',
                                        }}
                                    >
                                        {topicLabel(row.topic)}
                                    </Box>
                                    {displayedCandidates.map((candidate) => {
                                        const value = row[candidate] || 0;
                                        const count = row[`${candidate}_count`] || 0;
                                        const pct = row[`${candidate}_pct`] || 0;
                                        const intensity = heatmapData.maxValue > 0 ? value / heatmapData.maxValue : 0;

                                        return (
                                            <Box
                                                key={candidate}
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 90,
                                                    py: 1.5,
                                                    px: 0.5,
                                                    textAlign: 'center',
                                                    backgroundColor: getHeatmapColor(value, heatmapData.maxValue),
                                                    transition: 'all 0.2s ease',
                                                    cursor: 'default',
                                                    position: 'relative',
                                                    '&:hover': {
                                                        boxShadow: 'inset 0 0 0 2px #3B82F6',
                                                        zIndex: 1,
                                                    }
                                                }}
                                                title={t('heatmap.cell', { candidate, pct: pct.toFixed(1), n: count.toLocaleString() })}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: intensity > 0.4 ? 700 : 500,
                                                        color: intensity > 0.55 ? '#fff' : theme.palette.text.primary,
                                                        fontSize: 12,
                                                        textShadow: intensity > 0.5 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                                                    }}
                                                >
                                                    {viewMode === 'percentage' ? `${value.toFixed(1)}%` : value.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Color Legend */}
                    <Box sx={{
                        mt: 4,
                        pt: 3,
                        borderTop: 1, borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2
                    }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                            Menor concentración
                        </Typography>
                        <Box sx={{
                            width: 240,
                            height: 12,
                            background: 'linear-gradient(to right, #1C1B1F, #1E3A5F, #2563EB, #60A5FA)',
                            borderRadius: 6,
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                        }} />
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                            Mayor concentración
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

